<div class="bg-white dark:bg-slate-900 rounded-xl shadow dark:shadow-gray-700 p-4 sm:p-6">
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
    <div>
      <h2 class="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-1">Payment Information</h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">Step 5 of 6 • Secure Payment Processing</p>
    </div>
    <div class="mt-3 sm:mt-0">
      <div class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
        <i data-feather="shield" class="w-3 h-3 mr-1"></i>
        SSL Secured
      </div>
    </div>
  </div>

  <!-- Security Notice -->
  <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
    <div class="flex items-start">
      <i data-feather="shield-check" class="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0"></i>
      <div>
        <h3 class="text-sm font-medium text-green-900 dark:text-green-200 mb-1">Secure Payment</h3>
        <p class="text-sm text-green-700 dark:text-green-300">
          Your payment information is encrypted and secure. We use industry-standard SSL encryption to protect your data.
        </p>
      </div>
    </div>
  </div>

  <form [formGroup]="paymentForm" (ngSubmit)="onProcessPayment()">
    <!-- Payment Method Selection -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
        <i data-feather="credit-card" class="w-5 h-5 mr-2 text-blue-600"></i>
        Payment Method
      </h3>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <!-- Credit Card -->
        <div class="relative">
          <input
            type="radio"
            id="card"
            value="card"
            (change)="onPaymentMethodChange('card')"
            [checked]="selectedPaymentMethod === 'card'"
            class="sr-only">
          <label
            for="card"
            class="flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all"
            [ngClass]="{
              'border-blue-500 bg-blue-50 dark:bg-blue-900/20': selectedPaymentMethod === 'card',
              'border-gray-200 dark:border-gray-700 hover:border-gray-300': selectedPaymentMethod !== 'card'
            }">
            <i data-feather="credit-card" class="w-8 h-8 mb-2"
               [ngClass]="{
                 'text-blue-600': selectedPaymentMethod === 'card',
                 'text-gray-400': selectedPaymentMethod !== 'card'
               }"></i>
            <span class="text-sm font-medium text-slate-900 dark:text-white">Credit Card</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Visa, Mastercard, Amex</span>
          </label>
        </div>

        <!-- PayPal -->
        <div class="relative">
          <input
            type="radio"
            id="paypal"
            value="paypal"
            (change)="onPaymentMethodChange('paypal')"
            [checked]="selectedPaymentMethod === 'paypal'"
            class="sr-only">
          <label
            for="paypal"
            class="flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all"
            [ngClass]="{
              'border-blue-500 bg-blue-50 dark:bg-blue-900/20': selectedPaymentMethod === 'paypal',
              'border-gray-200 dark:border-gray-700 hover:border-gray-300': selectedPaymentMethod !== 'paypal'
            }">
            <i data-feather="dollar-sign" class="w-8 h-8 mb-2"
               [ngClass]="{
                 'text-blue-600': selectedPaymentMethod === 'paypal',
                 'text-gray-400': selectedPaymentMethod !== 'paypal'
               }"></i>
            <span class="text-sm font-medium text-slate-900 dark:text-white">PayPal</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Pay with PayPal</span>
          </label>
        </div>

        <!-- Bank Transfer -->
        <div class="relative">
          <input
            type="radio"
            id="bank"
            value="bank"
            (change)="onPaymentMethodChange('bank')"
            [checked]="selectedPaymentMethod === 'bank'"
            class="sr-only">
          <label
            for="bank"
            class="flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all"
            [ngClass]="{
              'border-blue-500 bg-blue-50 dark:bg-blue-900/20': selectedPaymentMethod === 'bank',
              'border-gray-200 dark:border-gray-700 hover:border-gray-300': selectedPaymentMethod !== 'bank'
            }">
            <i data-feather="home" class="w-8 h-8 mb-2"
               [ngClass]="{
                 'text-blue-600': selectedPaymentMethod === 'bank',
                 'text-gray-400': selectedPaymentMethod !== 'bank'
               }"></i>
            <span class="text-sm font-medium text-slate-900 dark:text-white">Bank Transfer</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">Direct bank payment</span>
          </label>
        </div>
      </div>
    </div>

    <!-- Credit Card Form -->
    <div *ngIf="selectedPaymentMethod === 'card'" class="space-y-6 mb-8">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
        <i data-feather="lock" class="w-5 h-5 mr-2 text-green-600"></i>
        Card Details
      </h3>

      <!-- Stripe Card Element -->
      <div>
        <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">Card Information</label>
        <div id="stripe-card-element" class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800">
          <!-- Stripe Elements will be mounted here -->
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Your payment information is encrypted and secure
        </p>
      </div>

      <!-- Cardholder Name -->
      <div>
        <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">Cardholder Name</label>
        <input
          type="text"
          placeholder="John Doe"
          formControlName="cardholderName"
          class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
          [class.border-red-500]="paymentForm.get('cardholderName')?.invalid && paymentForm.get('cardholderName')?.touched">
        <div *ngIf="paymentForm.get('cardholderName')?.invalid && paymentForm.get('cardholderName')?.touched"
             class="text-red-500 text-sm mt-1">
          Cardholder name is required
        </div>
      </div>

      <!-- Billing Address -->
      <div formGroupName="billingAddress">
        <h4 class="text-md font-medium text-slate-900 dark:text-white mb-4">Billing Address</h4>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">Street Address</label>
            <input
              type="text"
              formControlName="street"
              placeholder="123 Main Street"
              class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white">
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">City</label>
            <input
              type="text"
              formControlName="city"
              placeholder="New York"
              class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white">
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">State</label>
            <input
              type="text"
              formControlName="state"
              placeholder="NY"
              class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white">
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">ZIP Code</label>
            <input
              type="text"
              formControlName="zipCode"
              placeholder="10001"
              class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white">
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">Country</label>
            <select
              formControlName="country"
              class="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white">
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Save Card Option -->
      <div class="flex items-center">
        <input
          type="checkbox"
          id="saveCard"
          formControlName="saveCard"
          class="w-4 h-4 text-slate-900 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 checked:bg-slate-900 checked:border-slate-900 dark:text-slate-900 dark:bg-slate-700 dark:border-gray-600 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800">
        <label for="saveCard" class="ml-2 text-sm text-slate-900 dark:text-white">
          Save this card for future bookings
        </label>
      </div>
    </div>

    <!-- PayPal Payment -->
    <div *ngIf="selectedPaymentMethod === 'paypal'" class="mb-8">
      <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4 text-center">
          <i data-feather="shield" class="w-5 h-5 mr-2 inline"></i>
          PayPal Payment
        </h3>
        <p class="text-blue-700 dark:text-blue-300 mb-4 text-center">
          Click the PayPal button below to complete your payment securely.
        </p>

        <!-- PayPal Button Container -->
        <div id="paypal-button-container" class="max-w-md mx-auto">
          <!-- PayPal buttons will be rendered here -->
        </div>
      </div>
    </div>

    <!-- Bank Transfer -->
    <div *ngIf="selectedPaymentMethod === 'bank'" class="mb-8">
      <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-green-900 dark:text-green-200 mb-4 flex items-center justify-center">
          <i data-feather="home" class="w-5 h-5 mr-2"></i>
          Bank Transfer
        </h3>
        <div class="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4">
          <h4 class="font-medium text-slate-900 dark:text-white mb-3">Transfer Details:</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Bank Name:</span>
              <span class="font-medium">Harmonest Bank</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Account Number:</span>
              <span class="font-medium">1234567890</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Routing Number:</span>
              <span class="font-medium">987654321</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Reference:</span>
              <span class="font-medium">BOOKING-{{ Date.now() }}</span>
            </div>
          </div>
        </div>
        <p class="text-green-700 dark:text-green-300 text-sm text-center">
          Please include the reference number in your transfer. Processing may take 1-3 business days.
        </p>
      </div>
    </div>

    <!-- Terms and Conditions -->
    <div class="mb-8">
      <div class="flex items-start">
        <input
          type="checkbox"
          id="agreeToTerms"
          formControlName="agreeToTerms"
          class="w-4 h-4 text-slate-900 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 checked:bg-slate-900 checked:border-slate-900 dark:text-slate-900 dark:bg-slate-700 dark:border-gray-600 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 mt-1">
        <label for="agreeToTerms" class="ml-2 text-sm text-slate-900 dark:text-white">
          I agree to the <a href="#" class="text-blue-600 hover:text-blue-700 underline">Terms and Conditions</a>
          and <a href="#" class="text-blue-600 hover:text-blue-700 underline">Privacy Policy</a>
        </label>
      </div>
      <div *ngIf="paymentForm.get('agreeToTerms')?.invalid && paymentForm.get('agreeToTerms')?.touched"
           class="text-red-500 text-sm mt-1 ml-6">
        You must agree to the terms and conditions
      </div>
    </div>

    <!-- Payment Summary -->
    <div class="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 mb-8">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
        <i data-feather="receipt" class="w-5 h-5 mr-2 text-green-600"></i>
        Payment Summary
      </h3>
      <div class="flex justify-between items-center">
        <span class="text-lg font-medium text-slate-900 dark:text-white">Total Amount</span>
        <span class="text-2xl font-bold text-green-600">${{ totalAmount | number:'1.2-2' }}</span>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
        Includes all taxes and fees • No hidden charges
      </p>
    </div>

    <!-- Action Buttons -->
    <div class="flex flex-col sm:flex-row gap-3 sm:justify-between">
      <button
        type="button"
        (click)="onPrevious()"
        [disabled]="isProcessing"
        class="w-full sm:w-auto py-3 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-gray-500 hover:bg-gray-600 text-white rounded-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1">
        <i data-feather="arrow-left" class="w-4 h-4 mr-2 inline"></i>
        Back to Review
      </button>

      <button
        type="submit"
        [disabled]="!isFormValid || isProcessing"
        class="w-full sm:w-auto py-3 px-6 inline-block tracking-wide align-middle duration-500 text-base text-center bg-green-600 hover:bg-green-700 text-white rounded-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center">
        <div *ngIf="isProcessing" class="flex items-center">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          Processing...
        </div>
        <div *ngIf="!isProcessing" class="flex items-center">
          Complete Payment
          <i data-feather="arrow-right" class="w-4 h-4 ml-2"></i>
        </div>
      </button>
    </div>
  </form>
</div>
